import { c as _c } from "react/compiler-runtime";
import { useId } from "react";
import { cn } from "@/lib/utils";
interface HexagonPatternProps extends React.SVGProps<SVGSVGElement> {
  /**
   * The radius of each hexagon (center to vertex).
   * @default 40
   */
  radius?: number;
  /**
   * Spacing in pixels between adjacent hexagons.
   * The tile grows by this amount while the visual radius stays fixed,
   * so the gap is evenly distributed on all sides of each hexagon.
   * @default 0
   */
  gap?: number;
  /**
   * Offset applied to the pattern origin on the x-axis.
   * @default -1
   */
  x?: number;
  /**
   * Offset applied to the pattern origin on the y-axis.
   * @default -1
   */
  y?: number;
  /**
   * Controls the orientation of the hexagons.
   * - `"horizontal"` — flat-top hexagons tiled in a horizontal honeycomb grid.
   * - `"vertical"` — pointy-top hexagons tiled in a vertical honeycomb grid.
   * @default "horizontal"
   */
  direction?: "horizontal" | "vertical";
  /**
   * SVG stroke-dasharray applied to each hexagon outline.
   * @default "0"
   */
  strokeDasharray?: string;
  /**
   * Array of [col, row] coordinates for hexagons that should be highlighted
   * (filled) on top of the repeating pattern — mirrors the `squares` prop of
   * GridPattern.
   */
  hexagons?: Array<[col: number, row: number]>;
  className?: string;
  [key: string]: unknown;
}
type HexPoint = readonly [number, number];
function hexVertexList(cx: number, cy: number, r: number, direction: "horizontal" | "vertical"): HexPoint[] {
  const startAngle = direction === "horizontal" ? 0 : 30;
  return Array.from({
    length: 6
  }, (_, i) => {
    const angle = (startAngle + i * 60) * Math.PI / 180;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)] as const;
  });
}
function hexPoints(cx: number, cy: number, r: number, direction: "horizontal" | "vertical"): string {
  return hexVertexList(cx, cy, r, direction).map(([px, py]) => `${px.toFixed(3)},${py.toFixed(3)}`).join(" ");
}
function edgeLexKey(a: HexPoint, b: HexPoint): string {
  const [p, q] = a[0] < b[0] || a[0] === b[0] && a[1] <= b[1] ? [a, b] : [b, a];
  return `${p[0].toFixed(6)},${p[1].toFixed(6)}|${q[0].toFixed(6)},${q[1].toFixed(6)}`;
}
function collectUniqueHexEdges(centers: [number, number][], r: number, direction: "horizontal" | "vertical"): [HexPoint, HexPoint][] {
  const seen = new Set<string>();
  const edges: [HexPoint, HexPoint][] = [];
  for (const [cx, cy] of centers) {
    const verts = hexVertexList(cx, cy, r, direction);
    for (let i = 0; i < 6; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % 6];
      const key = edgeLexKey(a, b);
      if (!seen.has(key)) {
        seen.add(key);
        edges.push([a, b]);
      }
    }
  }
  return edges;
}
function isSolidStrokeDasharray(strokeDasharray: string): boolean {
  const t = strokeDasharray.trim();
  return t === "" || t === "none" || t === "0";
}
function getHexSpacing(r: number, direction: "horizontal" | "vertical", gap: number): {
  colStep: number;
  rowStep: number;
  tileW: number;
  tileH: number;
} {
  const sqrt3 = Math.sqrt(3);

  // `gap` should match the visible edge-to-edge spacing, so we add it along
  // the shared-edge normal instead of directly on the raw x/y axes.
  if (direction === "horizontal") {
    const colStep = 3 * r / 2 + sqrt3 * gap / 2;
    const rowStep = sqrt3 * r + gap;
    return {
      colStep,
      rowStep,
      tileW: colStep * 2,
      tileH: rowStep
    };
  }
  const colStep = sqrt3 * r + gap;
  const rowStep = 3 * r / 2 + sqrt3 * gap / 2;
  return {
    colStep,
    rowStep,
    tileW: colStep,
    tileH: rowStep * 2
  };
}
function getTileGeometry(r: number, direction: "horizontal" | "vertical", gap: number): {
  tileW: number;
  tileH: number;
  centers: [number, number][];
} {
  if (direction === "horizontal") {
    const {
      colStep,
      rowStep,
      tileW,
      tileH
    } = getHexSpacing(r, direction, gap);
    const canonical: [number, number][] = [[colStep / 2, rowStep / 2], [colStep * 3 / 2, rowStep]];
    const centers: [number, number][] = [];
    for (const [cx, cy] of canonical) {
      centers.push([cx, cy]);
      if (cy - r < 0) centers.push([cx, cy + tileH]);
      if (cy + r > tileH) centers.push([cx, cy - tileH]);
      if (cx - r < 0) centers.push([cx + tileW, cy]);
      if (cx + r > tileW) centers.push([cx - tileW, cy]);
      if (cy - r < 0 && cx - r < 0) centers.push([cx + tileW, cy + tileH]);
      if (cy - r < 0 && cx + r > tileW) centers.push([cx - tileW, cy + tileH]);
      if (cy + r > tileH && cx - r < 0) centers.push([cx + tileW, cy - tileH]);
      if (cy + r > tileH && cx + r > tileW) centers.push([cx - tileW, cy - tileH]);
    }
    return {
      tileW,
      tileH,
      centers
    };
  } else {
    const {
      colStep,
      rowStep,
      tileW,
      tileH
    } = getHexSpacing(r, direction, gap);
    const canonical: [number, number][] = [[colStep / 2, rowStep / 2], [colStep, rowStep * 3 / 2]];
    const centers: [number, number][] = [];
    for (const [cx, cy] of canonical) {
      centers.push([cx, cy]);
      if (cy - r < 0) centers.push([cx, cy + tileH]);
      if (cy + r > tileH) centers.push([cx, cy - tileH]);
      if (cx - r < 0) centers.push([cx + tileW, cy]);
      if (cx + r > tileW) centers.push([cx - tileW, cy]);
      if (cy - r < 0 && cx - r < 0) centers.push([cx + tileW, cy + tileH]);
      if (cy - r < 0 && cx + r > tileW) centers.push([cx - tileW, cy + tileH]);
      if (cy + r > tileH && cx - r < 0) centers.push([cx + tileW, cy - tileH]);
      if (cy + r > tileH && cx + r > tileW) centers.push([cx - tileW, cy - tileH]);
    }
    return {
      tileW,
      tileH,
      centers
    };
  }
}
function hexCenter(col: number, row: number, r: number, direction: "horizontal" | "vertical", gap: number): [number, number] {
  if (direction === "horizontal") {
    const {
      colStep,
      rowStep
    } = getHexSpacing(r, direction, gap);
    const x = col * colStep + colStep / 2;
    const y = row * rowStep + rowStep / 2 + (col % 2 !== 0 ? rowStep / 2 : 0);
    return [x, y];
  } else {
    const {
      colStep,
      rowStep
    } = getHexSpacing(r, direction, gap);
    const x = col * colStep + colStep / 2 + (row % 2 !== 0 ? colStep / 2 : 0);
    const y = row * rowStep + rowStep / 2;
    return [x, y];
  }
}
export function HexagonPattern(t0) {
  const $ = _c(57);
  let className;
  let hexagons;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== t0) {
    ({
      radius: t1,
      gap: t2,
      x: t3,
      y: t4,
      strokeDasharray: t5,
      direction: t6,
      hexagons,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = hexagons;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    className = $[1];
    hexagons = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  const radius = t1 === undefined ? 40 : t1;
  const gap = t2 === undefined ? 0 : t2;
  const x = t3 === undefined ? -1 : t3;
  const y = t4 === undefined ? -1 : t4;
  const strokeDasharray = t5 === undefined ? "0" : t5;
  const direction = t6 === undefined ? "horizontal" : t6;
  const id = useId();
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t7;
  let t8;
  let t9;
  if ($[10] !== className || $[11] !== direction || $[12] !== gap || $[13] !== id || $[14] !== props || $[15] !== radius || $[16] !== strokeDasharray || $[17] !== x || $[18] !== y) {
    const {
      tileW,
      tileH,
      centers
    } = getTileGeometry(radius, direction, gap);
    const solidStroke = isSolidStrokeDasharray(strokeDasharray);
    const dashedEdges = solidStroke ? null : collectUniqueHexEdges(centers, radius, direction);
    const fixedTileW = Number(tileW.toFixed(3));
    const fixedTileH = Number(tileH.toFixed(3));
    t14 = "true";
    t15 = true;
    if ($[30] !== className) {
      t16 = cn("pointer-events-none absolute inset-0 h-full w-full fill-gray-400/30 stroke-gray-400/30", className);
      $[30] = className;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    t17 = props;
    t7 = id;
    t8 = fixedTileW;
    t9 = fixedTileH;
    t10 = "userSpaceOnUse";
    t11 = x;
    t12 = y;
    t13 = solidStroke ? centers.map(t18 => {
      const [cx, cy] = t18;
      return <polygon className="fill-none" key={`${cx.toFixed(3)}-${cy.toFixed(3)}`} points={hexPoints(cx, cy, radius, direction)} strokeDasharray={strokeDasharray} />;
    }) : dashedEdges?.map(t19 => {
      const [a, b] = t19;
      return <line className="fill-none" key={edgeLexKey(a, b)} x1={a[0].toFixed(3)} x2={b[0].toFixed(3)} y1={a[1].toFixed(3)} y2={b[1].toFixed(3)} strokeDasharray={strokeDasharray} />;
    });
    $[10] = className;
    $[11] = direction;
    $[12] = gap;
    $[13] = id;
    $[14] = props;
    $[15] = radius;
    $[16] = strokeDasharray;
    $[17] = x;
    $[18] = y;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t12 = $[21];
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
    t17 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t18;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t18 = <defs><pattern id={t7} width={t8} height={t9} patternUnits={t10} x={t11} y={t12}>{t13}</pattern></defs>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = `url(#${id})`;
  let t20;
  if ($[40] !== t19) {
    t20 = <rect width="100%" height="100%" fill={t19} stroke="none" />;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== direction || $[43] !== gap || $[44] !== hexagons || $[45] !== radius || $[46] !== x || $[47] !== y) {
    t21 = hexagons && hexagons.length > 0 && <svg aria-hidden="true" className="overflow-visible" x={x} y={y}>{hexagons.map(t22 => {
        const [col, row] = t22;
        const [cx_0, cy_0] = hexCenter(col, row, radius, direction, gap);
        return <polygon key={`${col}-${row}`} points={hexPoints(cx_0, cy_0, radius - 1, direction)} strokeWidth="0" />;
      })}</svg>;
    $[42] = direction;
    $[43] = gap;
    $[44] = hexagons;
    $[45] = radius;
    $[46] = x;
    $[47] = y;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t14 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t20 || $[55] !== t21) {
    t22 = <svg aria-hidden={t14} suppressHydrationWarning={t15} className={t16} {...t17}>{t18}{t20}{t21}</svg>;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
}